import { useState, useCallback, useRef, useEffect } from 'react'
import type { Deck } from '@digital-twins/deck-ui/slides/contracts'
import { SlideFieldEdit } from '@digital-twins/deck-ui/slides/contracts'
import { deckApi, experimentsApi } from '@/lib/api/client'
import { isBackofficeStudyPreview } from '../../useBackofficeSummaryEdits'
import { applySlideFieldEdit } from './applySlideFieldEdit'

type SaveStatus = 'idle' | 'saving' | 'saved' | 'error'

const MAX_HISTORY = 50
const SAVED_DISPLAY_MS = 2000

interface DeckEditHistory {
  past: Array<Deck>
  present: Deck
  future: Array<Deck>
}

interface UseDeckEditingOptions {
  deck: Deck
  canEdit: boolean
  experimentId: string
  onDeckUpdate?: (deck: Deck) => void
}

export function useDeckEditing({
  deck,
  canEdit,
  experimentId,
  onDeckUpdate,
}: UseDeckEditingOptions) {
  const [isEditMode, setIsEditMode] = useState(false)
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle')
  const [history, setHistory] = useState<DeckEditHistory>({
    past: [],
    present: deck,
    future: [],
  })

  const savedDisplayTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const latestDeckRef = useRef(deck)
  const canEditRef = useRef(deck.canEdit)

  useEffect(() => {
    latestDeckRef.current = history.present
  }, [history.present])

  useEffect(() => {
    canEditRef.current = deck.canEdit
  }, [deck.canEdit])

  useEffect(() => {
    if (!isEditMode) {
      setHistory({ past: [], present: deck, future: [] })
    }
  }, [deck, isEditMode])

  const saveDeck = useCallback(async () => {
    const deckToSave = latestDeckRef.current
    setSaveStatus('saving')
    try {
      // Operators are blocked on PUT /experiments/:id/deck (platform route).
      // Back-office Preview must write the live deck via the admin mutation.
      if (isBackofficeStudyPreview()) {
        await experimentsApi.patchAdmin(experimentId, {
          op: 'update_artifact',
          artifactType: 'deck',
          value: deckToSave,
        })
      } else {
        await deckApi.updateDeck(experimentId, deckToSave)
      }
      setSaveStatus('saved')
      onDeckUpdate?.(deckToSave)
      if (savedDisplayTimerRef.current) {
        clearTimeout(savedDisplayTimerRef.current)
      }
      savedDisplayTimerRef.current = setTimeout(() => {
        setSaveStatus('idle')
      }, SAVED_DISPLAY_MS)
    } catch {
      setSaveStatus('error')
      throw new Error('Save failed')
    }
  }, [experimentId, onDeckUpdate])

  // Several fields of one slide written by one user edit: one history entry, so
  // one undo reverts them all.
  const editSlideFields = useCallback(
    (slideIndex: number, edits: Array<SlideFieldEdit>) => {
      setHistory((prev) => {
        const slides = [...prev.present.slides]
        if (slideIndex < 0 || slideIndex >= slides.length || edits.length === 0) return prev
        slides[slideIndex] = edits.reduce(
          (slide, edit) => applySlideFieldEdit(slide, edit.field, edit.value),
          slides[slideIndex] as unknown as Record<string, unknown>,
        ) as unknown as typeof slides[number]
        const nextDeck = { ...prev.present, slides }
        return {
          past: [...prev.past.slice(-(MAX_HISTORY - 1)), prev.present],
          present: nextDeck,
          future: [],
        }
      })
    },
    [],
  )

  const editSlideField = useCallback(
    (slideIndex: number, field: string, value: string) => {
      editSlideFields(slideIndex, [{ field, value }])
    },
    [editSlideFields],
  )

  const undo = useCallback(() => {
    setHistory((prev) => {
      if (prev.past.length === 0) return prev
      const previous = prev.past[prev.past.length - 1]
      return {
        past: prev.past.slice(0, -1),
        present: previous,
        future: [prev.present, ...prev.future].slice(0, MAX_HISTORY),
      }
    })
  }, [])

  const redo = useCallback(() => {
    setHistory((prev) => {
      if (prev.future.length === 0) return prev
      const next = prev.future[0]
      return {
        past: [...prev.past, prev.present].slice(-MAX_HISTORY),
        present: next,
        future: prev.future.slice(1),
      }
    })
  }, [])

  const toggleSlideVisibility = useCallback(
    (slideIndex: number) => {
      setHistory((prev) => {
        const metadata = { ...prev.present.metadata }
        const hidden = [...(metadata.hiddenSlideIndices || [])]
        const idx = hidden.indexOf(slideIndex)
        if (idx >= 0) {
          hidden.splice(idx, 1)
        } else {
          hidden.push(slideIndex)
        }
        metadata.hiddenSlideIndices = hidden
        const nextDeck = { ...prev.present, metadata }
        return {
          past: [...prev.past.slice(-(MAX_HISTORY - 1)), prev.present],
          present: nextDeck,
          future: [],
        }
      })
    },
    [],
  )

  const revertToOriginal = useCallback(async () => {
    try {
      setSaveStatus('saving')
      const response = await deckApi.revertDeck(experimentId)
      if (response.success && response.deck) {
        const reverted = { ...response.deck, canEdit: canEditRef.current } as Deck
        setHistory({ past: [], present: reverted, future: [] })
        onDeckUpdate?.(reverted)
        setSaveStatus('saved')
        if (savedDisplayTimerRef.current) {
          clearTimeout(savedDisplayTimerRef.current)
        }
        savedDisplayTimerRef.current = setTimeout(() => {
          setSaveStatus('idle')
        }, SAVED_DISPLAY_MS)
      } else {
        setSaveStatus('error')
      }
    } catch {
      setSaveStatus('error')
    }
  }, [experimentId, onDeckUpdate])

  const hasEdits = history.past.length > 0
  const isLeavingRef = useRef(false)

  // Editing ends when it stops being allowed (e.g. a new deck starts
  // generating): unsaved edits are saved first, as leaving edit mode does.
  useEffect(() => {
    if (canEdit || !isEditMode || isLeavingRef.current) {
      return
    }
    isLeavingRef.current = true
    const leave = async () => {
      if (hasEdits) {
        try {
          await saveDeck()
        } catch {
          // saveDeck already set the error status.
        }
      }
      setIsEditMode(false)
      isLeavingRef.current = false
    }
    void leave()
  }, [canEdit, isEditMode, hasEdits, saveDeck])

  const toggleEditMode = useCallback(async () => {
    if (!canEdit) return
    if (isEditMode) {
      try {
        await saveDeck()
      } catch {
        return
      }
    }
    setIsEditMode((prev) => !prev)
  }, [canEdit, isEditMode, saveDeck])

  useEffect(() => {
    return () => {
      if (savedDisplayTimerRef.current) clearTimeout(savedDisplayTimerRef.current)
    }
  }, [])

  return {
    isEditMode,
    toggleEditMode,
    currentDeck: history.present,
    saveStatus,
    canUndo: history.past.length > 0,
    canRedo: history.future.length > 0,
    undo,
    redo,
    editSlideField,
    editSlideFields,
    toggleSlideVisibility,
    revertToOriginal,
    hasEdits,
  }
}
